import React, { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { COLORS, COLORS_DARK, getDocCountsForLength } from '../../constants';
import { useTheme } from '../../context/ThemeContext';
import useResponsiveChart from '../../hooks/useResponsiveChart';
import CustomTooltip from '../CustomTooltip';
import CustomLegend from '../CustomLegend';
import ExplanationCard from '../ExplanationCard';

const MethodComparisonView = ({ selectedMethodsForComparison, comparisonModel, methodData, selectedLanguages }) => {
  const { isDarkMode } = useTheme();
  const colors = isDarkMode ? COLORS_DARK : COLORS;
  const { chartHeightLarge, chartMargin, axisLabelFontSize, tickFontSize, isMobile } = useResponsiveChart();
  const getMethodStrokePattern = (methodIndex) => {
    const patterns = ['0', '5 5', '10 5', '15 5 5 5', '20 5 5 5 5 5'];
    return patterns[methodIndex % patterns.length];
  };

  // Determine doc counts based on all selected methods' data lengths
  // If methods have different data lengths (e.g., one has 2 data points, another has 5),
  // use only [0, 10] for fair comparison
  const docCounts = useMemo(() => {
    if (!selectedMethodsForComparison.length || !comparisonModel) return [0, 10];

    const dataLengths = new Set();
    selectedMethodsForComparison.forEach(method => {
      if (methodData[method]?.[comparisonModel]) {
        selectedLanguages.forEach(lang => {
          const values = methodData[method][comparisonModel][lang];
          if (values && values.length > 0) {
            dataLengths.add(values.length);
          }
        });
      }
    });

    // If methods have different data lengths, use only [0, 10] for comparison
    if (dataLengths.size > 1) {
      return [0, 10];
    }

    // Otherwise, use the appropriate doc counts for the data length
    const length = dataLengths.size === 1 ? [...dataLengths][0] : 2;
    return getDocCountsForLength(length);
  }, [selectedMethodsForComparison, comparisonModel, methodData, selectedLanguages]);

  const comparisonData = useMemo(() => {
    if (!selectedMethodsForComparison.length || !comparisonModel) return [];

    return docCounts.map((docCount, docIndex) => {
      const dataPoint = { docCount };

      selectedMethodsForComparison.forEach(method => {
        if (methodData[method] && methodData[method][comparisonModel]) {
          selectedLanguages.forEach(lang => {
            const values = methodData[method][comparisonModel][lang] || [];
            // For mixed doc counts, only use first and last values (0 and 10 docs)
            let valueIndex = docIndex;
            if (docCounts.length === 2 && values.length > 2) {
              // Map 0 -> first value, 10 -> last value
              valueIndex = docIndex === 0 ? 0 : values.length - 1;
            }
            dataPoint[`${method}_${lang}`] = values[valueIndex] ?? null;
          });
        }
      });

      return dataPoint;
    });
  }, [selectedMethodsForComparison, comparisonModel, methodData, selectedLanguages, docCounts]);

  const comparisonStats = useMemo(() => {
    if (!selectedMethodsForComparison.length || !comparisonModel) return [];

    const stats = [];

    selectedMethodsForComparison.forEach(method => {
      if (methodData[method] && methodData[method][comparisonModel]) {
        selectedLanguages.forEach(lang => {
          const values = methodData[method][comparisonModel][lang] || [];
          const validValues = values.filter(val => val !== null && val !== undefined && !isNaN(val));

          if (validValues.length > 0) {
            const maxValue = Math.max(...validValues);
            const maxIndex = values.indexOf(maxValue);
            const minValue = Math.min(...validValues);
            const meanValue = validValues.reduce((a, b) => a + b, 0) / validValues.length;
            const improvement = validValues.length > 0 ? maxValue - (values[0] || 0) : 0;
            const methodDocCounts = getDocCountsForLength(values.length);

            stats.push({
              method,
              language: lang,
              maxScore: maxValue,
              minScore: minValue,
              meanScore: meanValue,
              optimalDocCount: methodDocCounts[maxIndex] ?? maxIndex,
              improvement,
              consistency: validValues.length > 1 ? Math.sqrt(validValues.map(v => Math.pow(v - meanValue, 2)).reduce((a, b) => a + b, 0) / (validValues.length - 1)) : 0
            });
          }
        });
      }
    });

    return stats;
  }, [selectedMethodsForComparison, comparisonModel, methodData, selectedLanguages]);

  const winnersAnalysis = useMemo(() => {
    if (!comparisonStats.length) return {};
    
    // Best overall performer
    const bestOverall = comparisonStats.reduce((best, current) => 
      current.maxScore > best.maxScore ? current : best
    );
    
    // Best per language
    const bestPerLanguage = {};
    selectedLanguages.forEach(lang => {
      const langStats = comparisonStats.filter(stat => stat.language === lang);
      if (langStats.length > 0) {
        bestPerLanguage[lang] = langStats.reduce((best, current) => 
          current.maxScore > best.maxScore ? current : best
        );
      }
    });
    
    // Most consistent performer
    const mostConsistent = comparisonStats.reduce((best, current) => 
      current.consistency < best.consistency ? current : best
    );
    
    // Best improvement
    const bestImprovement = comparisonStats.reduce((best, current) => 
      current.improvement > best.improvement ? current : best
    );
    
    return {
      bestOverall,
      bestPerLanguage,
      mostConsistent,
      bestImprovement
    };
  }, [comparisonStats, selectedLanguages]);

  if (selectedMethodsForComparison.length < 2 || !comparisonModel) {
    return (
      <div className="mb-8">
        <h3 className="text-3xl font-bold mb-3 text-center" style={{ color: 'var(--text-primary)' }}>
          Method Comparison Analysis
        </h3>
        <div className="text-center py-8">
          <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>Select at least 2 methods and a model to compare performance.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <ExplanationCard title="Method Comparison Explanation">
        <p><strong>Line Chart:</strong> Compares performance trajectories of selected methods across document counts. Different line patterns distinguish methods, colors represent languages.</p>
        <p><strong>Interpretation:</strong> Look for which method (line pattern) performs best for each language (color) across different document counts.</p>
        <p><strong>Line Patterns:</strong> Solid lines = first method, dashed lines = other methods with varying patterns.</p>
        {/* <p><strong>Best Practice:</strong> Compare methods at optimal document counts for each language to identify the most effective approach.</p> */}
      </ExplanationCard>

      <h3 className="text-3xl font-bold mb-3 text-center" style={{ color: 'var(--text-primary)' }}>
        Method Comparison Analysis
      </h3>
      <p className="text-lg text-center mb-8" style={{ color: 'var(--text-secondary)' }}>
        Comparing methods: {selectedMethodsForComparison.join(', ')} for {comparisonModel} model.
      </p>

      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={chartHeightLarge}>
          <LineChart data={comparisonData} margin={chartMargin}>
            <CartesianGrid strokeDasharray="4 4" stroke={isDarkMode ? '#333333' : '#e0e7ff'} />
            <XAxis
              dataKey="docCount"
              label={{ value: 'Number of Documents', position: 'bottom', offset: 40, fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: axisLabelFontSize, fontWeight: 'bold' }}
              tickLine={false}
              axisLine={{ stroke: isDarkMode ? '#404040' : '#cbd5e0' }}
              padding={{ left: 15, right: 15 }}
              tick={{ fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: tickFontSize }}
            />
            <YAxis
              label={{ value: 'Performance Score', angle: -90, position: 'insideLeft', offset: 10, fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: axisLabelFontSize, fontWeight: 'bold' }}
              tickLine={false}
              axisLine={{ stroke: isDarkMode ? '#404040' : '#cbd5e0' }}
              domain={['dataMin - 10', 'dataMax + 10']}
              tick={{ fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: tickFontSize }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} wrapperStyle={{ paddingTop: '40px' }} />
            {selectedMethodsForComparison.map((method, methodIdx) =>
              selectedLanguages.map(lang => {
                const dataKey = `${method}_${lang}`;
                return (
                  <Line
                    key={dataKey}
                    type="monotone"
                    dataKey={dataKey}
                    stroke={colors[lang]}
                    strokeWidth={3}
                    strokeDasharray={getMethodStrokePattern(methodIdx)}
                    dot={{ r: 6, fill: colors[lang] }}
                    name={`${method} (${lang})`}
                    connectNulls={false}
                  />
                );
              })
            )}
          </LineChart>
        </ResponsiveContainer>

        {/* Legend explanation for line patterns */}
        <div className="mt-6 p-4 rounded-lg border" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}>
          <h5 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Line Pattern Guide:</h5>
          <div className="flex flex-wrap gap-4 text-xs" style={{ color: 'var(--text-secondary)' }}>
            {selectedMethodsForComparison.map((method, index) => (
              <div key={method} className="flex items-center">
                <svg width="32" height="8" className="mr-2">
                  <line
                    x1="0"
                    y1="4"
                    x2="32"
                    y2="4"
                    stroke={isDarkMode ? '#a3a3a3' : '#4a5568'}
                    strokeWidth="2"
                    strokeDasharray={getMethodStrokePattern(index)}
                  />
                </svg>
                <span>{method}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Winners Analysis */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Best Performers */}
        <div className="p-6 rounded-xl shadow-lg border" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}>
          <h4 className="text-xl font-semibold mb-4 text-center" style={{ color: 'var(--text-primary)' }}>Best Performers</h4>
          <div className="space-y-4">
            <div className="border-l-4 border-yellow-500 pl-4 py-2">
              <h5 className="font-medium" style={{ color: 'var(--text-primary)' }}>Overall Champion</h5>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                <span className="font-semibold">{winnersAnalysis.bestOverall?.method}</span> - {winnersAnalysis.bestOverall?.language}
                <br />Score: {winnersAnalysis.bestOverall?.maxScore?.toFixed(2)}
                @ {winnersAnalysis.bestOverall?.optimalDocCount} docs
              </p>
            </div>

            <div className="border-l-4 border-green-500 pl-4 py-2">
              <h5 className="font-medium" style={{ color: 'var(--text-primary)' }}>Most Consistent</h5>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                <span className="font-semibold">{winnersAnalysis.mostConsistent?.method}</span> - {winnersAnalysis.mostConsistent?.language}
                <br />Std Dev: {winnersAnalysis.mostConsistent?.consistency?.toFixed(2)}
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4 py-2">
              <h5 className="font-medium" style={{ color: 'var(--text-primary)' }}>Best Improvement</h5>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                <span className="font-semibold">{winnersAnalysis.bestImprovement?.method}</span> - {winnersAnalysis.bestImprovement?.language}
                <br />Gain: +{winnersAnalysis.bestImprovement?.improvement?.toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Language Winners */}
        <div className="p-6 rounded-xl shadow-lg border" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}>
          <h4 className="text-xl font-semibold mb-4 text-center" style={{ color: 'var(--text-primary)' }}>Best per Language</h4>
          <div className="space-y-3">
            {Object.entries(winnersAnalysis.bestPerLanguage || {}).map(([lang, winner]) => (
              <div key={lang} className="flex justify-between items-center p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                <div>
                  <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{lang}</span>
                  <br />
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{winner.method}</span>
                </div>
                <div className="text-right">
                  <div className="font-bold text-lg" style={{ color: colors[lang] }}>
                    {winner.maxScore.toFixed(1)}
                  </div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{winner.optimalDocCount} docs</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Comprehensive Comparison Table / Cards */}
      <div className="mt-8 p-6 rounded-xl shadow-lg border" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}>
        <h4 className="text-xl font-semibold mb-4 text-center" style={{ color: 'var(--text-primary)' }}>Detailed Comparison</h4>

        {isMobile ? (
          <div className="space-y-3">
            {comparisonStats.map((stat, index) => (
              <div key={index} className="p-3 rounded-lg border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{stat.method}</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: colors[stat.language] + '20', color: colors[stat.language] }}>
                    {stat.language}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-muted)' }}>Max</span>
                    <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{stat.maxScore.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-muted)' }}>Mean</span>
                    <span style={{ color: 'var(--text-primary)' }}>{stat.meanScore.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-muted)' }}>Min</span>
                    <span style={{ color: 'var(--text-primary)' }}>{stat.minScore.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-muted)' }}>Docs</span>
                    <span style={{ color: 'var(--text-primary)' }}>{stat.optimalDocCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-muted)' }}>Gain</span>
                    <span className="font-medium" style={{ color: stat.improvement >= 0 ? 'var(--success-500)' : 'var(--error-500)' }}>
                      {stat.improvement >= 0 ? '+' : ''}{stat.improvement.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-muted)' }}>StdDev</span>
                    <span style={{ color: 'var(--text-primary)' }}>{stat.consistency.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr style={{ backgroundColor: 'var(--bg-secondary)' }}>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Method</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Language</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Max Score</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Mean Score</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Min Score</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Optimal Docs</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Improvement</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Consistency</th>
                </tr>
              </thead>
              <tbody>
                {comparisonStats.map((stat, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 0 ? 'var(--bg-secondary)' : 'transparent' }}>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{stat.method}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            style={{ backgroundColor: colors[stat.language] + '20', color: colors[stat.language] }}>
                        {stat.language}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{stat.maxScore.toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>{stat.meanScore.toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>{stat.minScore.toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>{stat.optimalDocCount}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium" style={{ color: stat.improvement >= 0 ? 'var(--success-500)' : 'var(--error-500)' }}>
                      {stat.improvement >= 0 ? '+' : ''}{stat.improvement.toFixed(2)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>{stat.consistency.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MethodComparisonView;